import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Package, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";

// Image URLs for all products
const IMAGES = {
  // Raw Cotton Category - Accurate cotton product images
  cottonYarn: "https://images.pexels.com/photos/745761/pexels-photo-745761.jpeg?auto=compress&w=800",
  cottonWaste: "https://images.unsplash.com/photo-1638213628789-c2d3df4d6c8d?w=800&q=80",
  cottonNoil: "https://images.pexels.com/photos/13717225/pexels-photo-13717225.jpeg?auto=compress&w=800",
  cottonLickerin: "https://images.pexels.com/photos/35081362/pexels-photo-35081362.jpeg?auto=compress&w=800",
  
  // Seeds & Nuts Category - Accurate seed images
  foxNuts: "https://images.pexels.com/photos/7051132/pexels-photo-7051132.jpeg?auto=compress&w=800",
  chiaSeeds: "https://images.unsplash.com/photo-1549530708-be1b34900690?w=800&q=80",
  whiteSesame: "https://images.pexels.com/photos/30162371/pexels-photo-30162371.jpeg?auto=compress&w=800",
  blackSesame: "https://images.pexels.com/photos/8951051/pexels-photo-8951051.jpeg?auto=compress&w=800",
  basilSeeds: "https://images.unsplash.com/photo-1648859477796-5219c34bf46d?w=800&q=80",
  mustardSeeds: "https://images.pexels.com/photos/18697673/pexels-photo-18697673.jpeg?auto=compress&w=800",
  sunflowerSeeds: "https://images.unsplash.com/photo-1760544140120-bd64261f455a?w=800&q=80",
  pumpkinSeeds: "https://images.pexels.com/photos/1080071/pexels-photo-1080071.jpeg?auto=compress&w=800",
  
  // Dehydrated Category - Accurate dried onion/garlic images
  dehydratedChopped: "https://images.unsplash.com/photo-1606172150255-64e15da65c8c?w=800&q=80",
  dehydratedGranules: "https://images.pexels.com/photos/7956510/pexels-photo-7956510.jpeg?auto=compress&w=800",
  dehydratedFlakes: "https://images.pexels.com/photos/26823080/pexels-photo-26823080.jpeg?auto=compress&w=800",
  dehydratedMinced: "https://images.unsplash.com/photo-1682338401236-2482c7200a8b?w=800&q=80",
  dehydratedPowder: "https://images.unsplash.com/photo-1697546312799-e46bcbd73ed6?w=800&q=80",
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

// Product Specification Template Component
const ProductSpecTemplate = ({ specs }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mt-4">
      <CollapsibleTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="w-full flex items-center justify-between border-border hover:bg-surface text-xs"
          data-testid="spec-toggle"
        >
          <span className="flex items-center gap-2">
            <FileText className="w-3 h-3" />
            Specifications
          </span>
          {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3">
        <div className="bg-surface rounded-lg p-3 space-y-1.5 text-xs">
          {specs.map((spec, index) => (
            <div key={index} className="flex justify-between py-1 border-b border-border last:border-0">
              <span className="text-muted-foreground">{spec.label}</span>
              <span className="font-medium text-foreground text-right">{spec.value}</span>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Product Card Component
const ProductCard = ({ product }) => {
  return (
    <Card className="product-card overflow-hidden border-0 shadow-lg h-full flex flex-col group">
      <div className="relative h-44 img-container overflow-hidden">
        <img 
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
      <CardContent className="p-4 flex-1 flex flex-col">
        <h3 className="font-heading font-semibold text-base text-primary mb-1">
          {product.name}
        </h3>
        <p className="text-muted-foreground text-sm mb-3 flex-1 line-clamp-2">
          {product.description}
        </p>
        
        {product.specs && <ProductSpecTemplate specs={product.specs} />}
        
        <Link to="/contact" className="mt-3">
          <Button 
            className="w-full btn-secondary text-white font-medium text-sm py-2"
            data-testid={`quote-btn-${product.id}`}
          >
            Request Quote
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
};

// Category Section Component
const CategorySection = ({ category, products }) => {
  return (
    <section id={category.id} className="py-12 scroll-mt-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
            <Package className="w-6 h-6 text-secondary" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-primary">
              {category.name}
            </h2>
            <p className="text-muted-foreground text-sm">{category.description}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
            >
              <ProductCard product={product} />
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

const ProductsPage = () => {
  const categories = [
    { id: 'raw-cotton', name: 'Raw Cotton', description: 'Premium quality cotton and cotton by-products for textile industry' },
    { id: 'seeds-nuts', name: 'Seeds & Nuts', description: 'High-grade seeds and nuts for food processing and agriculture' },
    { id: 'dehydrated', name: 'Dehydrated Onion & Garlic', description: 'Export-quality dehydrated vegetables in various forms' },
  ];

  const productData = {
    'raw-cotton': [
      {
        id: 'cotton-yarn',
        name: 'Cotton Yarn',
        image: IMAGES.cottonYarn,
        description: 'High-quality combed and carded cotton yarn in various counts for textile manufacturing.',
        specs: [
          { label: 'Origin', value: 'India' },
          { label: 'Count Range', value: '20s - 60s' },
          { label: 'Type', value: 'Carded/Combed' },
          { label: 'Moisture', value: 'Max 8%' },
          { label: 'Packaging', value: 'Cones (1.89kg)' },
          { label: 'MOQ', value: '10 MT' },
          { label: 'Payment Terms', value: 'LC/TT' },
        ]
      },
      {
        id: 'cotton-waste',
        name: 'Cotton Waste',
        image: IMAGES.cottonWaste,
        description: 'Recycled cotton waste suitable for spinning, padding, and non-woven applications.',
        specs: [
          { label: 'Origin', value: 'India' },
          { label: 'Type', value: 'Soft/Hard Waste' },
          { label: 'Purity', value: 'Min 85%' },
          { label: 'Moisture', value: 'Max 10%' },
          { label: 'Packaging', value: 'Pressed Bales' },
          { label: 'MOQ', value: '20 MT' },
          { label: 'Payment Terms', value: 'LC at Sight' },
        ]
      },
      {
        id: 'cotton-noil',
        name: 'Cotton Noil',
        image: IMAGES.cottonNoil,
        description: 'Short fiber cotton noil for non-woven fabrics, medical supplies, and absorbent products.',
        specs: [
          { label: 'Origin', value: 'India' },
          { label: 'Fiber Length', value: 'Up to 12mm' },
          { label: 'Purity', value: 'Min 90%' },
          { label: 'Moisture', value: 'Max 8%' },
          { label: 'Packaging', value: 'Compressed Bales' },
          { label: 'MOQ', value: '15 MT' },
          { label: 'Payment Terms', value: 'LC/TT' },
        ]
      },
      {
        id: 'cotton-lickerin',
        name: 'Cotton Lickerin',
        image: IMAGES.cottonLickerin,
        description: 'Premium cotton lickerin waste for yarn recycling and industrial textile applications.',
        specs: [
          { label: 'Origin', value: 'India' },
          { label: 'Type', value: 'Lickerin Waste' },
          { label: 'Purity', value: 'Min 80%' },
          { label: 'Moisture', value: 'Max 10%' },
          { label: 'Packaging', value: 'Pressed Bales' },
          { label: 'MOQ', value: '25 MT' },
          { label: 'Payment Terms', value: 'LC at Sight' },
        ]
      },
    ],
    'seeds-nuts': [
      {
        id: 'fox-nuts',
        name: 'Fox Nuts (Makhana)',
        image: IMAGES.foxNuts,
        description: 'Premium puffed lotus seeds, a popular healthy snack and ingredient for Indian cuisine.',
        specs: [
          { label: 'Origin', value: 'Bihar, India' },
          { label: 'Grade', value: '4-Sutta / 5-Sutta' },
          { label: 'Moisture', value: 'Max 12%' },
          { label: 'Packaging', value: '10/20kg PP Bags' },
          { label: 'MOQ', value: '5 MT' },
          { label: 'Supply Capacity', value: '100 MT/month' },
          { label: 'Payment Terms', value: 'LC/TT' },
        ]
      },
      {
        id: 'chia-seeds',
        name: 'Chia Seeds',
        image: IMAGES.chiaSeeds,
        description: 'Nutrient-rich chia seeds ideal for health food industry and bakery applications.',
        specs: [
          { label: 'Origin', value: 'India' },
          { label: 'Purity', value: 'Min 99%' },
          { label: 'Moisture', value: 'Max 8%' },
          { label: 'Color', value: 'Black/White' },
          { label: 'Packaging', value: '25kg PP Bags' },
          { label: 'MOQ', value: '10 MT' },
          { label: 'Payment Terms', value: 'LC/TT' },
        ]
      },
      {
        id: 'white-sesame',
        name: 'White Sesame Seeds',
        image: IMAGES.whiteSesame,
        description: 'Premium hulled white sesame seeds for oil extraction, confectionery, and food processing.',
        specs: [
          { label: 'Origin', value: 'Gujarat, Rajasthan' },
          { label: 'Purity', value: 'Min 99.95%' },
          { label: 'Moisture', value: 'Max 5%' },
          { label: 'Oil Content', value: 'Min 48%' },
          { label: 'Packaging', value: '25/50kg PP Bags' },
          { label: 'MOQ', value: '20 MT' },
          { label: 'Payment Terms', value: 'LC at Sight' },
        ]
      },
      {
        id: 'black-sesame',
        name: 'Black Sesame Seeds',
        image: IMAGES.blackSesame,
        description: 'Natural black sesame seeds rich in antioxidants, perfect for Asian cuisine and health foods.',
        specs: [
          { label: 'Origin', value: 'India' },
          { label: 'Purity', value: 'Min 99.90%' },
          { label: 'Moisture', value: 'Max 6%' },
          { label: 'Oil Content', value: 'Min 46%' },
          { label: 'Packaging', value: '25/50kg PP Bags' },
          { label: 'MOQ', value: '15 MT' },
          { label: 'Payment Terms', value: 'LC/TT' },
        ]
      },
      {
        id: 'basil-seeds',
        name: 'Basil Seeds (Sabja)',
        image: IMAGES.basilSeeds,
        description: 'High-quality basil seeds for beverages, desserts, and traditional medicine applications.',
        specs: [
          { label: 'Origin', value: 'India' },
          { label: 'Purity', value: 'Min 99%' },
          { label: 'Moisture', value: 'Max 10%' },
          { label: 'Color', value: 'Black' },
          { label: 'Packaging', value: '20/25kg Bags' },
          { label: 'MOQ', value: '5 MT' },
          { label: 'Payment Terms', value: 'LC/TT' },
        ]
      },
      {
        id: 'mustard-seeds',
        name: 'Mustard Seeds',
        image: IMAGES.mustardSeeds,
        description: 'Yellow and brown mustard seeds for oil production, condiments, and spice blends.',
        specs: [
          { label: 'Origin', value: 'Rajasthan, MP' },
          { label: 'Variety', value: 'Yellow/Brown' },
          { label: 'Purity', value: 'Min 99%' },
          { label: 'Moisture', value: 'Max 8%' },
          { label: 'Packaging', value: '50kg PP Bags' },
          { label: 'MOQ', value: '25 MT' },
          { label: 'Payment Terms', value: 'LC at Sight' },
        ]
      },
      {
        id: 'sunflower-seeds',
        name: 'Sunflower Seeds',
        image: IMAGES.sunflowerSeeds,
        description: 'Raw and roasted sunflower seed kernels for snacking, baking, and oil extraction.',
        specs: [
          { label: 'Origin', value: 'India' },
          { label: 'Type', value: 'Kernels/In-Shell' },
          { label: 'Purity', value: 'Min 99%' },
          { label: 'Moisture', value: 'Max 8%' },
          { label: 'Packaging', value: '25kg Vacuum Pack' },
          { label: 'MOQ', value: '10 MT' },
          { label: 'Payment Terms', value: 'LC/TT' },
        ]
      },
      {
        id: 'pumpkin-seeds',
        name: 'Pumpkin Seeds',
        image: IMAGES.pumpkinSeeds,
        description: 'Premium green pumpkin seed kernels, a nutritious superfood for health-conscious consumers.',
        specs: [
          { label: 'Origin', value: 'India' },
          { label: 'Grade', value: 'AA / A / B' },
          { label: 'Moisture', value: 'Max 8%' },
          { label: 'Color', value: 'Green' },
          { label: 'Packaging', value: '25kg Vacuum Pack' },
          { label: 'MOQ', value: '5 MT' },
          { label: 'Payment Terms', value: 'LC/TT' },
        ]
      },
    ],
    'dehydrated': [
      {
        id: 'dehy-chopped',
        name: 'Chopped Onion & Garlic',
        image: IMAGES.dehydratedChopped,
        description: 'Dehydrated chopped onion and garlic pieces, perfect for instant soups and ready meals.',
        specs: [
          { label: 'Origin', value: 'Maharashtra, India' },
          { label: 'Size', value: '3-5mm / 5-8mm' },
          { label: 'Moisture', value: 'Max 6%' },
          { label: 'Color', value: 'White/Off-white' },
          { label: 'Packaging', value: '10/20kg Cartons' },
          { label: 'MOQ', value: '5 MT' },
          { label: 'Payment Terms', value: 'LC/TT' },
        ]
      },
      {
        id: 'dehy-granules',
        name: 'Onion & Garlic Granules',
        image: IMAGES.dehydratedGranules,
        description: 'Fine dehydrated granules ideal for seasoning blends, sauces, and food processing.',
        specs: [
          { label: 'Origin', value: 'India' },
          { label: 'Mesh Size', value: '8-16 / 16-26 / 26-40' },
          { label: 'Moisture', value: 'Max 5%' },
          { label: 'Allicin (Garlic)', value: 'Min 0.3%' },
          { label: 'Packaging', value: '10/25kg Cartons' },
          { label: 'MOQ', value: '5 MT' },
          { label: 'Payment Terms', value: 'LC/TT' },
        ]
      },
      {
        id: 'dehy-flakes',
        name: 'Onion & Garlic Flakes',
        image: IMAGES.dehydratedFlakes,
        description: 'Large dehydrated flakes with excellent rehydration, used in cooking and snack manufacturing.',
        specs: [
          { label: 'Origin', value: 'Maharashtra, India' },
          { label: 'Size', value: '10-15mm / 15-25mm' },
          { label: 'Moisture', value: 'Max 6%' },
          { label: 'Color', value: 'White/Pink' },
          { label: 'Packaging', value: '10/20kg Cartons' },
          { label: 'MOQ', value: '5 MT' },
          { label: 'Payment Terms', value: 'LC/TT' },
        ]
      },
      {
        id: 'dehy-minced',
        name: 'Minced Onion & Garlic',
        image: IMAGES.dehydratedMinced,
        description: 'Finely minced dehydrated onion and garlic for instant flavor in convenience foods.',
        specs: [
          { label: 'Origin', value: 'India' },
          { label: 'Size', value: '1-3mm' },
          { label: 'Moisture', value: 'Max 5%' },
          { label: 'Bulk Density', value: '400-500 g/L' },
          { label: 'Packaging', value: '10/25kg Cartons' },
          { label: 'MOQ', value: '5 MT' },
          { label: 'Payment Terms', value: 'LC/TT' },
        ]
      },
      {
        id: 'dehy-powder',
        name: 'Onion & Garlic Powder',
        image: IMAGES.dehydratedPowder,
        description: 'Ultra-fine powder form for instant dissolution in sauces, dressings, and seasonings.',
        specs: [
          { label: 'Origin', value: 'India' },
          { label: 'Mesh', value: '80-100 / 100-120' },
          { label: 'Moisture', value: 'Max 5%' },
          { label: 'Allicin (Garlic)', value: 'Min 0.3%' },
          { label: 'Packaging', value: '20/25kg Drums' },
          { label: 'MOQ', value: '5 MT' },
          { label: 'Payment Terms', value: 'LC/TT' },
        ]
      },
    ],
  };

  return (
    <div data-testid="products-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-primary">
        <div className="container-custom">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-accent font-medium text-sm tracking-wider uppercase">Our Products</span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mt-3 mb-6">
              Export-Grade Agricultural Products
            </h1>
            <p className="text-white/80 text-lg leading-relaxed">
              Premium quality cotton, seeds, nuts and dehydrated products sourced from India's 
              finest agricultural regions. All products meet international export standards.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-6 bg-surface border-b sticky top-20 z-30">
        <div className="container-custom">
          <div className="flex flex-wrap gap-3">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                className="px-5 py-2.5 rounded-full bg-white border border-border hover:border-secondary hover:bg-secondary/5 text-sm font-medium text-primary transition-all duration-300 hover:shadow-md"
                data-testid={`cat-nav-${cat.id}`}
              >
                {cat.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Products Sections */}
      <div className="container-custom pb-12">
        {categories.map((category) => (
          <CategorySection 
            key={category.id} 
            category={category} 
            products={productData[category.id]} 
          />
        ))}
      </div>

      {/* CTA Section */}
      <section className="py-16 bg-secondary">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white mb-4">
              Need Custom Specifications?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              We can source and process products according to your specific requirements. 
              Contact us with your detailed specifications.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-white text-secondary hover:bg-white/90 font-semibold px-8"
                data-testid="products-cta-btn"
              >
                Request Custom Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
